// ============================================================================
// The consent screen's two calls (item #853): read what Claude is asking for,
// and answer it. They go to Supabase's sign-in server with the app's own pass,
// the way the app already calls its functions (facility-summary).
//
// ⛔ Not supabase.auth.oauth.*: in supabase-js those look for a Supabase sign-in
// session in the client first and, finding none, answer "Auth session missing"
// without calling the server. This app has no such session by design — Firebase
// signs people in and the bridge's pass rides every request (shared/supabase.ts).
// The server itself accepts that pass: proven 2026-10-14 by a read and a deny
// signed the app's way (~/.tcs-scratch/oauth-consent-endpoints-probe-2026-10-14.mjs).
//
// ⛔ A FAILED READ IS NEVER AN EMPTY SCREEN: each answer is data or a sentence.
// ============================================================================
import { getSupabaseToken, SUPABASE_ANON_KEY } from '../../shared/supabase';
import { errorMessage } from '../../shared/errors';
import type { Read } from './api';

const AUTH = `${import.meta.env.VITE_SUPABASE_URL as string}/auth/v1/oauth/authorizations`;

export interface AuthorizationDetails {
  authorization_id: string;
  redirect_uri?: string;
  client?: { client_name?: string; name?: string } | null;
  user?: { email?: string } | null;
  scope?: string;
  /** Present when this sign-in already allowed this app: Supabase answers with where to go. */
  redirect_url?: string;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<Read<T>> {
  const token = getSupabaseToken();
  if (!token) return { ok: false, message: 'You are not signed in.' };
  let res: Response;
  try {
    res = await fetch(`${AUTH}/${path}`, {
      ...init,
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
  } catch (e) {
    return { ok: false, message: `The sign-in server could not be reached. ${errorMessage(e, '')}`.trim() };
  }
  const body = (await res.json().catch(() => null)) as (T & { msg?: string; message?: string; error_description?: string }) | null;
  if (!res.ok || !body) {
    // The code and the server's own words go to the console; the screen says what to do.
    console.warn('[build-room/connect] sign-in server answered', res.status, body?.msg ?? body?.message ?? body?.error_description ?? '');
    if (res.status === 404) return { ok: false, message: 'This request from Claude has expired or was already answered. Start again from Claude.' };
    return { ok: false, message: 'The sign-in server would not accept this. Start again from Claude, and if it happens again, tell Ben.' };
  }
  return { ok: true, data: body };
}

export const readAuthorization = (id: string) => call<AuthorizationDetails>(encodeURIComponent(id));

export const answerAuthorization = (id: string, action: 'approve' | 'deny') =>
  call<{ redirect_url: string }>(`${encodeURIComponent(id)}/consent`, { method: 'POST', body: JSON.stringify({ action }) });
